/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import ClinicianActivities from '../ClinicianView/ClinicianActivities';
import ClinicianTreatments from '../ClinicianView/ClinicianTreatments';
import AssignmentDetails from '../ClinicianView/AssignmentDetails/AssignmentDetails';
import ClinicianDetails from '../ClinicianView/ClinicianDetails/ClinicianDetails';
import { ClinicianCards } from '../ClinicianView/ClinicianCards';
import CopyTreatment from '../ClinicianView/CopyTreatment/CopyTreatment';
import { ClinicianTreatmentDetails } from '../ClinicianView/UnassignedTreatments/ClinicianTreatmentDetails';
import NonPatientActivityDetails from '../ClinicianView/AssignmentDetails/NonPatientActivityDetails';
import TreatmentInstances from '../TreatmentView/TreatmentInstances';
import TimelineDialog from './TimelineDialog';
import useTimeline from '../timeline/useTimeline';
import _ from '../../../utils/lodash';
import globals from '../../../globals/communityGlobals';
import { parseDateUtc, timeIsBetweenDateRange } from '../../../utils/dates';
import {
  timelineGridHtml,
  renderSchedulerHourColumns,
  sortClinicianOptions,
  sortingIconMap,
} from './timelineConstants';
import {
  getElementPropsByClass,
  getElementsByClass,
  setElementProp,
  setTimelineHeight,
  getSchedulerPeriod,
  handleScrolls,
  getTravelMinutesFromElement,
} from './timelineHelper';

// eslint-disable-next-line react/prop-types
export default function TimelineView({ settings, getClinicianTreatmentInstances, filteredInstances }) {
  const {
    getFlag,
    activeAssignment,
    activeTreatmentInstanceForCopy,
    activeUnassignedTreatment,
    activeNonPatientAssignment,
    activeClinicianId,
  } = useTimeline();
  const [clinciianFilterOption, setClinciianFilterOption] = useState(1);
  const [clinciianSortingOptions, setClinicianSortingOptions] = useState(sortClinicianOptions);
  const [isActionRunning, setIsActionRunning] = useState(false);
  const [forceReloadClinicianTreatments, setForceReloadClinicianTreatments] = useState(false);
  const [isMultiAssign, setIsMultiAssign] = useState(false);
  const [timelineDialogContent, setTimelineDialogContent] = useState({ content: null, refreshOnClose: false });

  const selectedSortingOption = clinciianSortingOptions.find(x => x.id === clinciianFilterOption);
  const isAsc = selectedSortingOption.isAsc;
  const locale = globals.getArea('dailyPlanning');
  const schedulerPeriod = getSchedulerPeriod(settings.period);

  const forceRefreshClinicianTreatments = () => {
    setForceReloadClinicianTreatments(!forceReloadClinicianTreatments);
  };

  const handleClinicianIsDirty = index => {
    if (settings.dataItems.length < index) return false;
    const availabilities = settings.dataItems[index].activities.availabilities.filter(item => {
      const isValidStartTime = timeIsBetweenDateRange(
        { dateFrom: schedulerPeriod.startDate, dateTo: schedulerPeriod.endDate },
        item.visual.startDate,
      );

      if (!isValidStartTime) {
        return timeIsBetweenDateRange(
          { dateFrom: schedulerPeriod.startDate, dateTo: schedulerPeriod.endDate },
          item.visual.endDate,
        );
      }

      return isValidStartTime;
    });

    return availabilities.length === 0;
  };

  const shouldHideClinician = index =>
    handleClinicianIsDirty(index) && settings.dataItems[index].activities.assignments.length === 0;

  const clinicians = [...settings.dataItems].filter((item, i) => !shouldHideClinician(i));
  clinicians.sort((a, b) => {
    const propertyName = selectedSortingOption.propertyName;
    if (isAsc && a.cardDetails[propertyName] > b.cardDetails[propertyName]) return 1;
    if (!isAsc && a.cardDetails[propertyName] < b.cardDetails[propertyName]) return 1;
    if (isAsc && a.cardDetails[propertyName] < b.cardDetails[propertyName]) return -1;
    if (!isAsc && a.cardDetails[propertyName] > b.cardDetails[propertyName]) return -1;
    return 0;
  });

  const setFilterValue = (name, value) => {
    setClinciianFilterOption(value);
    const sortedList = clinciianSortingOptions.map(cl => {
      if (cl.id === value) {
        return {
          ...cl,
          name: (
            <span>
              {`✓ ${cl.label}`} <i className={!cl.isAsc ? sortingIconMap.ASC : sortingIconMap.DESC} />
            </span>
          ),
          isAsc: !cl.isAsc,
        };
      }
      return {
        ...cl,
        name: cl.label,
        isAsc: false,
      };
    });
    setClinicianSortingOptions(sortedList);
  };

  const hasData = settings.dataItems.length > 0;
  let dataLength = Array.from({ length: settings.dataItems.length }, (v, k) => k);
  const hoursArray = Array.from({ length: schedulerPeriod.duration }, (v, i) => {
    let h = schedulerPeriod.startHour + i;
    if (h >= 24) {
      h = Math.abs(h - 24);
    }
    return h;
  });

  dataLength = dataLength.map((a, v) => ({
    key: v,
    dontRender: shouldHideClinician(v),
    isDirty: handleClinicianIsDirty(v),
  }));

  const handleTimelineRender = () => {
    const schedule = {
      options: {
        containerId: 'timeline-schedule',
        h_width: getElementPropsByClass('timeline-hour-row').width, // get a width of hour div
        cell_duration: 60, // lesson duration (minutes)
        startTime: () => schedulerPeriod.startHour, // start at 6.00 AM
        endTime: () => schedulerPeriod.endHour, // end at 6.00 AM next day
        minScheduleTime: () => schedulerPeriod.startDate,
        maxScheduleTime: () => schedulerPeriod.endDate,
        minToPx() {
          // divide the box width by the duration of one lesson
          return schedule.options.h_width / schedule.options.cell_duration;
        },
      },
      general: {
        roundNumber(num) {
          return Math.round((num + Number.EPSILON) * 10) / 10;
        },
        getPosition(start, duration) {
          const translateX =
            moment.duration(start.diff(schedule.options.minScheduleTime())).asMinutes() * schedule.options.minToPx();
          const width = duration * schedule.options.minToPx();

          return [schedule.general.roundNumber(width), schedule.general.roundNumber(translateX)];
        },
        isToday: () => {
          const currentTime = parseDateUtc(globals.now);
          return currentTime.isBetween(schedule.options.minScheduleTime(), schedule.options.maxScheduleTime());
        },
      },
      setAssignmentsPosition: () => {
        // set visits position and width
        const schedulerAssignmentItems = getElementsByClass(schedule.options.containerId, '.timeline-assignment');
        _.each(schedulerAssignmentItems, item => {
          const borderOffset = 1;
          const startDateTime = parseDateUtc(item.startDateTime);
          const paddingOffset = startDateTime.isAfter(schedule.options.minScheduleTime()) ? 8 : 0;

          const [width, translateX] = schedule.general.getPosition(startDateTime, item.duration);
          const visitWidth = width - borderOffset;

          setElementProp(item.element, 'transform', `translateX(${translateX + borderOffset}px)`);
          setElementProp(item.element, 'width', `${visitWidth}px`);
          item.element.classList.remove('initial-render');

          // set visit travel time duration as Px
          const travelTimeElement = getTravelMinutesFromElement(item.element, '.visit-travel-section');
          let travelTimeWidthTotal = 0;
          if (travelTimeElement.duration > 0) {
            const [travelTimeWidth] = schedule.general.getPosition(startDateTime, travelTimeElement.duration);
            travelTimeWidthTotal = travelTimeWidth - paddingOffset;
            setElementProp(travelTimeElement.element, 'width', `${travelTimeWidthTotal}px`);
          }

          // set visit content width as Px
          const visitContentElement = item.element.querySelectorAll('.visit-content');
          if (visitContentElement && visitContentElement.length > 0) {
            setElementProp(visitContentElement[0], 'width', `${visitWidth - travelTimeWidthTotal - paddingOffset}px`);
          }
        });
      },
      setAvailabilityPosition: () => {
        // set availability/empty placeholders position and width
        const schedulerAvailabilityItems = getElementsByClass(
          schedule.options.containerId,
          '.timeline-availability-placeholder',
        );

        const activeTimeslots = [];
        _.each(schedulerAvailabilityItems, item => {
          const borderOffset = 1;
          const startDateTime = parseDateUtc(item.startDateTime);
          const [width, translateX] = schedule.general.getPosition(startDateTime, item.duration);

          setElementProp(item.element, 'transform', `translateX(${translateX + 1}px)`);
          setElementProp(item.element, 'width', `${width - borderOffset}px`);

          if (item.element.classList.contains('available')) {
            activeTimeslots.push({
              isAssignable: item.element.classList.contains('non-assignable'),
              translateX,
              translateY: 0,
            });
          }
        });

        const timelineContainer = getElementsByClass(schedule.options.containerId, '.timeline-rows-container');
        if (timelineContainer.length > 0 && timelineContainer[0].element) {
          const timelineContainerElement = timelineContainer[0].element;
          if (activeTimeslots.length > 0) {
            const sortedTimeslots = _.orderBy(
              activeTimeslots,
              ['isAssignable', 'translateX', 'translateY'],
              ['asc', 'asc', 'asc'],
            );
            const leftOffset = 70;
            const positionX =
              leftOffset > sortedTimeslots[0].translateX ? 0 : sortedTimeslots[0].translateX - leftOffset;

            if (typeof timelineContainerElement.scroll === 'function') {
              timelineContainerElement.scroll(positionX, 0);
            } else {
              timelineContainerElement.scrollLeft = positionX;
            }
          }
        }
      },
      handleTimelineHeight: () => {
        const heightOffset =
          28 + // THIS NEED TO BE RE-VISIT after UI is completely done
          getElementPropsByClass('page-header').height +
          getElementPropsByClass('summary-hours-wrapper').height +
          getElementPropsByClass('clinicianview-filters-section').height +
          getElementPropsByClass('scheduler-header-section').height +
          getElementPropsByClass('timeline-head-info').height;

        setTimelineHeight(schedule.options.containerId, heightOffset); // Set timeline container Height
        handleScrolls(schedule.options.containerId); // Handle scrolls to timeline Body, Cards column and Header
      },
      setCurrentTimePosition: () => {
        // set currenttime line position and visibility
        const element = document.getElementById('current-time-wrapper');

        if (element) {
          if (schedule.general.isToday()) {
            const currentTime = parseDateUtc(globals.now);
            const currentTimeUtc = globals.now.utc();
            const minMinutes = currentTime.isSame(schedule.options.minScheduleTime(), 'day') // condition after midnight
              ? schedule.options.minScheduleTime().hours() * 60 + schedule.options.minScheduleTime().minutes()
              : currentTimeUtc.diff(currentTime, 'minutes');

            const nowMinutes = currentTime.hours() * 60 + currentTime.minutes();
            const translateX = (nowMinutes - minMinutes) * schedule.options.minToPx();

            setElementProp(element, 'transform', `translateX(${translateX}px)`);
            setElementProp(element, 'display', 'block');

            const lineHeight =
              getElementPropsByClass('timeline-activities').height >
              getElementPropsByClass('timeline-rows-container').height
                ? getElementPropsByClass('timeline-rows-container').scrollHeight - 25
                : getElementPropsByClass('timeline-rows-container').height - 35;
            setElementProp(element.querySelectorAll('.current-time-line')[0], 'height', `${lineHeight}px`);
          } else {
            setElementProp(element, 'display', 'none');
          }
        }
      },
      setHighlightRow: () => {
        // set currenttime line position and visibility
        const element = document.getElementsByClassName('highlight-row');

        if (element && element.length > 0) {
          setElementProp(element[0], 'width', `${getElementPropsByClass('timeline-rows-container').scrollWidth}px`);
        }
      },
      initialize: () => {
        schedule.setAssignmentsPosition();
        schedule.setAvailabilityPosition();
        schedule.handleTimelineHeight();
        setTimeout(schedule.setCurrentTimePosition, 100);
        setTimeout(schedule.setHighlightRow, 100);
      },
    };

    if (!settings.isUpdateTriggered) schedule.initialize();

    const funcExist = window.recalculate && typeof window.recalculate === 'function';
    if (!funcExist) window.recalculate = schedule.handleTimelineHeight;
  };

  const clinicianIds = _.map(settings.clinicians, 'id');
  useEffect(() => {
    handleTimelineRender();
  }, [settings.isUpdateTriggered, getFlag('showTravelTime'), getFlag('isEditMode'), activeClinicianId, clinicianIds]);

  const dataItemsRender = () => {
    if (hasData) {
      return (
        <div className="timeline-rows-container tbody">
          <div id="current-time-wrapper">
            <Tooltip title="Current">
              <div className="current-time-heading" />
            </Tooltip>
            <div className="current-time-line">&nbsp;</div>
          </div>
          <div className="timeline-activities">
            {clinicians.map((item, index) => (
              <ClinicianActivities
                key={`activity-${index}`}
                isMileageCapEnabled={settings.isMileageCapEnabled}
                activities={item.activities}
                isAdvanceFilterActive={filteredInstances.isAdvanceFilterActive}
                filteredAssignments={filteredInstances.filteredAssignments}
                shouldFilterAssignments={filteredInstances.shouldFilterAssignments}
                schedulerPeriod={schedulerPeriod}
                clinicianIds={clinicianIds}
                baseRequestModel={settings.baseRequestModel}
                onActionClick={settings.toggleForceRefresh}
                isActionRunning={isActionRunning}
                setIsActionRunning={setIsActionRunning}
                isMultiAssign={isMultiAssign}
                setIsMultiAssign={setIsMultiAssign}
                setTimelineDialog={setTimelineDialogContent}
              />
            ))}
          </div>
          {hoursArray.map((item, index) => renderSchedulerHourColumns(index, dataLength))}
        </div>
      );
    }
    return (
      <div className="no-data-container no-margins">
        <span className="no-data-content">{locale.getMsg('noCliniciansAvailable')}</span>
      </div>
    );
  };

  const onTimelineDialogClose = () => {
    if (timelineDialogContent.refreshOnClose) {
      settings.toggleForceRefresh();
    }
    setTimelineDialogContent({ content: null, refreshOnClose: false });
  };

  const renderClinicianTreatmentList = () => {
    const isTreatmentView = settings.includeTreatments;

    if (isTreatmentView) {
      return (
        <React.Fragment>
          <TreatmentInstances
            treatmentInstances={settings.treatmentInstances}
            filteredTreatmentInstances={filteredInstances.filteredTreatmentInstances}
            filteredCancelledTreatmentInstances={filteredInstances.filteredCancelledTreatmentInstances}
            isAdvanceFilterActive={filteredInstances.isAdvanceFilterActive}
            baseRequestModel={settings.baseRequestModel}
            clinicianIds={clinicianIds}
            toggleRefresh={settings.toggleForceRefresh}
            includeTreatments={settings.includeTreatments}
            isNotClickable={isActionRunning || settings.isUpdateTriggered}
            isMultiAssign={isMultiAssign}
            setIsMultiAssign={setIsMultiAssign}
          />
          <ClinicianCards
            clinicians={clinicians}
            isMileageCapEnabled={settings.isMileageCapEnabled}
            setFilterValue={setFilterValue}
            clinciianFilterOption={clinciianFilterOption}
            clinciianSortingOptions={clinciianSortingOptions}
            includeTreatments={isTreatmentView}
          />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <ClinicianCards
          clinicians={clinicians}
          isMileageCapEnabled={settings.isMileageCapEnabled}
          setFilterValue={setFilterValue}
          clinciianFilterOption={clinciianFilterOption}
          clinciianSortingOptions={clinciianSortingOptions}
          includeTreatments={isTreatmentView}
        />
        {getFlag('showClinicianTreatments') && (
          <ClinicianTreatments
            baseRequestModel={settings.baseRequestModel}
            getClinicianTreatmentInstances={getClinicianTreatmentInstances}
            filteredClinicianTreatmentInstances={filteredInstances.filteredClinicianTreatmentInstances}
            isAdvanceFilterActive={filteredInstances.isAdvanceFilterActive}
            forceReloadClinicianTreatments={forceReloadClinicianTreatments}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <div id="timeline-schedule" className="total-wrapper" style={{ overflow: 'unset' }}>
        {renderClinicianTreatmentList()}
        {/* Timeline scheduler grid content */}
        <div className="timeline-container timeline-block rest-columns">
          <div className="thead">{timelineGridHtml(hoursArray)}</div>
          {dataItemsRender()}
        </div>
      </div>
      {activeAssignment !== null && (
        <AssignmentDetails
          refreshGrid={settings.toggleForceRefresh}
          period={settings.period}
          isMileageCapEnabled={settings.isMileageCapEnabled}
          includeTreatments={settings.includeTreatments}
        />
      )}
      {activeUnassignedTreatment !== null && (
        <ClinicianTreatmentDetails
          refreshGrid={settings.toggleForceRefresh}
          includeTreatments={settings.includeTreatments}
          period={settings.period}
          isBundled={activeUnassignedTreatment.isBundled}
          forceRefreshClinicianTreatments={forceRefreshClinicianTreatments}
        />
      )}
      {getFlag('showClinicianDetails') && (
        <ClinicianDetails
          isMileageCapEnabled={settings.isMileageCapEnabled}
          period={settings.period}
          autoAllocateCallback={settings.toggleForceRefresh}
        />
      )}
      {activeTreatmentInstanceForCopy !== null && (
        <CopyTreatment settings={settings} baseRequestModel={settings.baseRequestModel} />
      )}
      {activeNonPatientAssignment !== null && (
        <NonPatientActivityDetails
          period={settings.period}
          refreshGrid={settings.toggleForceRefresh}
          isMileageCapEnabled={settings.isMileageCapEnabled}
        />
      )}
      {!!timelineDialogContent.content && (
        <TimelineDialog
          bodyContent={timelineDialogContent.content}
          onClose={onTimelineDialogClose}
          setIsActionRunning={setIsActionRunning}
        />
      )}
    </React.Fragment>
  );
}
